import { useGetEmployeeLookupsByRolesQuery, Role } from '@app/main/api';
import { useUserContextSelector } from '@app/main/utils';

export interface ChatAllowedMembersResult {
  /** IDs of accounts the current user is allowed to chat with. `undefined` = no filter (Coordinator). */
  allowedIds: string[] | undefined;
  /** IDs to pre-select on the form. Set for Patient/CHS only. */
  preSelectedIds: string[] | undefined;
  /** Whether the member-picker UI should be hidden (Patient/CHS). */
  hideMemberList: boolean;
  isLoading: boolean;
}

const NO_FILTER: ChatAllowedMembersResult = {
  allowedIds: undefined,
  preSelectedIds: undefined,
  hideMemberList: false,
  isLoading: false,
};

const COORDINATOR_ONLY: Role[] = ['COORDINATOR'];
const EXCLUDE_PATIENTS_AND_CHS: Role[] = ['COORDINATOR', 'ADMIN', 'PERFORMER', 'SECURITY'];

// Most permissive role wins when a user has multiple roles.
const ROLE_PRIORITY: Role[] = [
  'COORDINATOR',
  'GLOBAL_ADMIN',
  'ADMIN',
  'PERFORMER',
  'SECURITY',
  'CARE_HOME_STAFF',
  'PATIENT',
];

interface RolePolicy {
  /** Roles to fetch from getEmployeeLookups. `undefined` means no fetch (no filter applied). */
  rolesToFetch: Role[] | undefined;
  hideMemberList: boolean;
  preSelectAll: boolean;
}

const ROLE_POLICY: Partial<Record<Role, RolePolicy>> = {
  COORDINATOR:     { rolesToFetch: undefined,                hideMemberList: false, preSelectAll: false },
  GLOBAL_ADMIN:    { rolesToFetch: EXCLUDE_PATIENTS_AND_CHS, hideMemberList: false, preSelectAll: false },
  ADMIN:           { rolesToFetch: EXCLUDE_PATIENTS_AND_CHS, hideMemberList: false, preSelectAll: false },
  PERFORMER:       { rolesToFetch: EXCLUDE_PATIENTS_AND_CHS, hideMemberList: false, preSelectAll: false },
  SECURITY:        { rolesToFetch: EXCLUDE_PATIENTS_AND_CHS, hideMemberList: false, preSelectAll: false },
  CARE_HOME_STAFF: { rolesToFetch: COORDINATOR_ONLY,         hideMemberList: true,  preSelectAll: true  },
  PATIENT:         { rolesToFetch: COORDINATOR_ONLY,         hideMemberList: true,  preSelectAll: true  },
};

export function useChatAllowedMemberIds(): ChatAllowedMembersResult {
  const roles = useUserContextSelector((x) => x.roles) as Role[];

  const matchedRole = ROLE_PRIORITY.find((r) => roles.includes(r));
  const policy = matchedRole ? ROLE_POLICY[matchedRole] : undefined;

  // Single query call. skip when no fetch is needed (Coordinator or unmapped role).
  const { data, isLoading } = useGetEmployeeLookupsByRolesQuery(
    { roles: policy?.rolesToFetch, onlyActive: true },
    { skip: !policy?.rolesToFetch },
  );

  if (!policy || !policy.rolesToFetch) {
    return NO_FILTER;
  }

  const ids = data?.map((x) => x.id);
  return {
    allowedIds: ids,
    preSelectedIds: policy.preSelectAll ? ids : undefined,
    hideMemberList: policy.hideMemberList,
    isLoading,
  };
}
